import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BaseBotData,
  BotData,
  BotType,
  NumberOption,
  Options,
  OverrideFieldType,
} from "@shared/types";
import {
  MarginData,
  PendingLeverageType,
  PendingMarginType,
  PendingMultiplier,
  SymbolToUpdatedMarginData,
} from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/types";
import { MultiSymbolMarginContext } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMargin.context";
import { checkSymbolSearch } from "@shared/utils/misc";
import { isSymbol } from "@shared/types/utils";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import {
  BotFormContext,
  BotFormData,
} from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  getBotMultiSymbolMarginData,
  getDefaultGlobalSettings,
  getDefaultMarginData,
  getHasCustomSymbols,
} from "@shared/features/bots/utils";
import {
  BaseBotFormValues,
  BotFormType,
  LeverageType,
  MultiSymbolMarginData,
  SymbolMarginData,
} from "@shared/features/bots/BotForm/common/types";
import {
  channelValue,
  MARGIN_GLOBAL_SETTINGS_KEY,
} from "@shared/features/bots/BotForm/common/constants";
import { isEqual, mapValues } from "lodash";
import { getSymbolMarginPreview } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/utils";
import { useTheme } from "styled-components";
import { toggleSetItem } from "@shared/utils/arrays";
import { sortByNumberProperty } from "@shared/utils/general";

interface MultiSymbolMarginProviderProps {
  isViewOnly?: boolean;
  botData?: BaseBotData | BotData;
  botFormData?: BotFormData;
  isSymbolsOnly?: boolean;
  alwaysDisplay?: boolean;
  lastModifiedData?: MultiSymbolMarginData;
  updateLastModifiedData?: (
    updatedLastModifiedData: MultiSymbolMarginData
  ) => void;
  isFullScreen?: boolean;
}

const MultiSymbolMarginProvider = ({
  children,
  alwaysDisplay,
  isSymbolsOnly,
  isViewOnly,
  botData,
  botFormData: propsBotFormData,
  lastModifiedData,
  updateLastModifiedData: propsUpdateLastModifiedData,
  isFullScreen,
}: PropsWithChildren<MultiSymbolMarginProviderProps>) => {
  const theme = useTheme();

  const [pendingMarginType, setPendingMarginType] =
    useState<PendingMarginType>();
  const [pendingMultiplier, setPendingMultiplier] =
    useState<PendingMultiplier>();
  const [pendingLeverageType, setPendingLeverageType] =
    useState<PendingLeverageType>();
  const [selectedSymbols, setSelectedSymbols] = useState<Set<string>>(
    new Set()
  );
  const [modifiedPendingValues, setModifiedPendingValues] = useState(
    () => new Set<keyof MarginData>()
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [globalSettingsExpanded, setGlobalSettingsExpanded] = useState(
    !isViewOnly
  );
  const [
    lastModifiedMultiSymbolMarginData,
    setLastModifiedMultiSymbolMarginData,
  ] = useState<MultiSymbolMarginData>(lastModifiedData ?? {});
  const [initializedExchange, setInitializedExchange] = useState<string>();
  const [initializedBotGeneralData, setInitializedBotGeneralData] =
    useState<BaseBotFormValues["multiSymbolMarginData"]>();

  const botFormContextData = useContext(BotFormContext);
  const botFormDataRef = useRef<BotFormData | undefined>(undefined);
  botFormDataRef.current = propsBotFormData || botFormContextData;

  const {
    botType: formBotType,
    multiSymbolMarginData: formMultiSymbolMarginData,
    isMarginExchange,
    botGeneralData,
    account: formAccount,
    botFormType,
    publicBotGeneralData,
    backtestBotData,
    editBotGeneralData,
    isBacktesting,
    symbolsMetadata: formSymbolsMetadata,
    setMultiSymbolMarginData,
    toggleOverrideField,
    getBotCreateGeneralDataStatus,
  } = botFormDataRef.current || {};
  const botType = botData?.bot_type ?? formBotType;
  const botOptions = botGeneralData?.options || botData?.options;

  const { getAccountById } = useClientsData();
  const account = botData?.client_id
    ? getAccountById(botData.client_id)
    : formAccount;
  const symbolsMetadata = useSymbolsMetadata();
  const { getAccountActiveSymbols, getSymbolMetadata } =
    formSymbolsMetadata || symbolsMetadata;
  const isCopyPublic = botFormType === BotFormType.COPY_PUBLIC;
  const isEdit = botFormType === BotFormType.EDIT;
  const isDuplicate = botFormType === BotFormType.DUPLICATE;
  const isSignalsBot = botType === BotType.SIGNAL;

  const allSymbols = useMemo(() => {
    const accountSymbols = isSymbol(account)
      ? []
      : getAccountActiveSymbols(account?.client_id) || [];
    if (isSymbolsOnly) {
      if (botData?.symbols) return botData.symbols;
      if (isSignalsBot && botData?.blacklist_symbols?.length) {
        const blacklistSet = new Set(botData.blacklist_symbols);
        return accountSymbols?.filter((symbol) => !blacklistSet.has(symbol));
      }
    }
    return accountSymbols;
  }, [getAccountActiveSymbols, account, botData]);

  const resetData = (
    resetGlobal?: boolean,
    resetSymbols?: boolean,
    updatedSymbols?: Set<string>
  ) => {
    setSelectedSymbols(updatedSymbols ?? new Set());
    const globalSettings = multiSymbolMarginData?.[MARGIN_GLOBAL_SETTINGS_KEY];
    if (resetGlobal) {
      setPendingGlobalMarginType(globalSettings?.marginType ?? channelValue);
      setPendingGlobalMultiplier(globalSettings?.multiplier ?? channelValue);
      setPendingGlobalLeverageType(
        globalSettings?.leverageType ?? channelValue
      );
    }
    if (resetSymbols) {
      setTimeout(() => {
        setPendingMarginType(undefined);
        setPendingMultiplier(undefined);
        setPendingLeverageType(undefined);
        setModifiedPendingValues(new Set());
      }, 600);
    }
  };

  const { originalBotMarginData, originalBotExchange } = useMemo(() => {
    let baseBotData: BaseBotData | undefined;
    if (isBacktesting) {
      baseBotData = backtestBotData;
    } else if (isCopyPublic && publicBotGeneralData) {
      baseBotData = publicBotGeneralData;
    } else if ((isEdit || isDuplicate) && editBotGeneralData) {
      baseBotData = editBotGeneralData;
    }
    const originalBotMarginData = baseBotData
      ? getBotMultiSymbolMarginData(baseBotData)[0]
      : undefined;
    return {
      originalBotMarginData,
      originalBotExchange: baseBotData?.exchange,
    };
  }, [
    publicBotGeneralData,
    editBotGeneralData,
    backtestBotData,
    isBacktesting,
    botFormType,
  ]);

  const getGlobalOptions = (optionsData?: { [p: string]: Options }) => {
    if (!optionsData) return;
    const valuesSet = new Set<number>(undefined);
    const allOptions: NumberOption[] = [];
    Object.values(optionsData).forEach(([options]) => {
      options.forEach((option) => {
        if (!valuesSet.has(option.value)) {
          valuesSet.add(option.value);
          allOptions.push(option);
        }
      });
    });
    return allOptions.sort((a, b) => sortByNumberProperty(a.value, b.value));
  };

  const getSymbolMarginMetadata = (symbol: string) => {
    const isGlobalSettings = symbol === MARGIN_GLOBAL_SETTINGS_KEY;
    const multiplierOptionsData = botOptions?.Multiplier?.[0];
    const multiplierOptions = isGlobalSettings
      ? getGlobalOptions(multiplierOptionsData)
      : multiplierOptionsData?.[symbol]?.[0];
    const marginTypeOptionsData = botOptions?.["Margin Type"]?.[0];
    const marginTypeOptions = isGlobalSettings
      ? getGlobalOptions(marginTypeOptionsData)
      : marginTypeOptionsData?.[symbol]?.[0];
    const maxMultiplier =
      multiplierOptions?.[multiplierOptions.length - 1]?.value;
    const marginTypes = marginTypeOptions?.map((option) => option.value);
    const multipliers = multiplierOptions?.map((option) => option.value);

    return {
      marginTypeOptions: marginTypeOptions,
      marginTypes,
      marginTypesSet: new Set(marginTypes),
      multiplierOptions,
      multipliers,
      multipliersSet: new Set(multipliers),
      maxMultiplier,
    };
  };

  const symbolToMarginMetadata = useMemo(() => {
    const marginTypeMapping = (botGeneralData || botData)?.options?.[
      "Margin Type"
    ]?.[0];
    const metadata = mapValues(marginTypeMapping, (_, symbol) =>
      getSymbolMarginMetadata(symbol)
    );
    metadata[MARGIN_GLOBAL_SETTINGS_KEY] = getSymbolMarginMetadata(
      MARGIN_GLOBAL_SETTINGS_KEY
    );
    return metadata;
  }, [botGeneralData]);

  const getSymbolInitMarginData = (symbol: string): SymbolMarginData => {
    const { multipliersSet, multipliers, maxMultiplier } =
      symbolToMarginMetadata?.[symbol] || {};

    const symbolBaseMarginData = isSymbol(originalBotMarginData)
      ? undefined
      : originalBotMarginData?.[symbol];
    const lastModifiedSymbolMarginData =
      lastModifiedMultiSymbolMarginData?.[symbol];
    const defaultMarginData: Partial<SymbolMarginData> = {};
    (["marginType", "multiplier", "leverageType"] as const).forEach(
      (marginKey) => {
        let defaultValue =
          lastModifiedSymbolMarginData?.[marginKey] ??
          symbolBaseMarginData?.[marginKey] ??
          channelValue;
        if (marginKey === "multiplier" && !isSymbol(defaultValue)) {
          if (maxMultiplier && defaultValue > maxMultiplier)
            defaultValue = maxMultiplier;
          else if (!multipliersSet?.has(defaultValue)) {
            const validMultiplier = multipliers
              ?.slice()
              .reverse()
              .find(
                (multiplier) =>
                  !isSymbol(defaultValue) && multiplier < defaultValue
              );
            if (validMultiplier) defaultValue = validMultiplier;
          }
        }
        defaultMarginData[marginKey] = (defaultValue ?? channelValue) as any;
      }
    );
    return defaultMarginData as SymbolMarginData;
  };

  const getInitData = (isAllChannel?: boolean) => {
    if (!isMarginExchange) return channelValue;
    const initMultiSymbolMarginData: MultiSymbolMarginData = {};
    let hasNoneChannelValue = false;
    if (!isAllChannel) {
      allSymbols?.forEach((symbol) => {
        const symbolInOriginalBotData =
          originalBotMarginData &&
          !isSymbol(originalBotMarginData) &&
          symbol in originalBotMarginData;
        const symbolInitData = isAllChannel
          ? getDefaultMarginData()
          : getSymbolInitMarginData(symbol);
        hasNoneChannelValue =
          hasNoneChannelValue ||
          Object.values(symbolInitData).some((value) => !isSymbol(value));
        if (symbolInOriginalBotData) {
          initMultiSymbolMarginData[symbol] = symbolInitData;
        }
      });
    }
    const initGlobalSettings = isAllChannel
      ? getDefaultMarginData()
      : getSymbolInitMarginData(MARGIN_GLOBAL_SETTINGS_KEY);
    initMultiSymbolMarginData[MARGIN_GLOBAL_SETTINGS_KEY] = initGlobalSettings;
    hasNoneChannelValue =
      hasNoneChannelValue ||
      Object.values(initGlobalSettings).some((value) => !isSymbol(value));
    const defaultValue = getDefaultGlobalSettings();
    return !isAllChannel &&
      !hasNoneChannelValue &&
      !getHasCustomSymbols(initMultiSymbolMarginData)
      ? defaultValue
      : initMultiSymbolMarginData;
  };

  const getShouldInitializeData = () => {
    const base =
      !isFullScreen &&
      isSignalsBot &&
      !!botGeneralData &&
      !getBotCreateGeneralDataStatus?.isFetching &&
      !!account;
    return (
      base &&
      (!initializedExchange ||
        (!isSymbol(account) && initializedExchange !== account.exchange) ||
        initializedBotGeneralData !== originalBotMarginData)
    );
  };

  useEffect(() => {
    if (isSymbol(formMultiSymbolMarginData)) {
      setPendingGlobalMarginType(channelValue);
      setPendingGlobalMultiplier(channelValue);
      setPendingGlobalLeverageType(channelValue);
    }
  }, [formMultiSymbolMarginData]);

  const getMultiSymbolMarginData = () => {
    if (isSymbol(formMultiSymbolMarginData)) return getInitData(true);
    else if (formMultiSymbolMarginData) return formMultiSymbolMarginData;
    else if (botData?.symbol_to_leverage_data) {
      return getBotMultiSymbolMarginData(botData)[0];
    }
  };

  const multiSymbolMarginData = useMemo(() => {
    const multiSymbolMarginData = getMultiSymbolMarginData();
    return isSymbol(multiSymbolMarginData) ? undefined : multiSymbolMarginData;
  }, [allSymbols, formMultiSymbolMarginData, botData?.symbol_to_leverage_data]);

  const resetMarginData = formMultiSymbolMarginData && !isMarginExchange;
  useEffect(() => {
    if (resetMarginData) {
      setMultiSymbolMarginData?.(channelValue);
      toggleOverrideField?.(OverrideFieldType.LEVERAGE, false);
    }
  }, [resetMarginData]);

  const getDefaultShowModifiedSymbols = () => {
    return !isSymbolsOnly && getHasCustomSymbols(multiSymbolMarginData || {});
  };

  const [showOnlyModifiedSymbols, setShowOnlyModifiedSymbols] = useState(
    getDefaultShowModifiedSymbols
  );

  const globalSettings =
    multiSymbolMarginData?.[MARGIN_GLOBAL_SETTINGS_KEY] ??
    getDefaultMarginData();
  const [pendingGlobalMarginType, setPendingGlobalMarginType] =
    useState<PendingMarginType>(globalSettings?.marginType ?? channelValue);
  const [pendingGlobalMultiplier, setPendingGlobalMultiplier] =
    useState<PendingMultiplier>(globalSettings?.multiplier ?? channelValue);
  const [pendingGlobalLeverageType, setPendingGlobalLeverageType] =
    useState<PendingLeverageType>(globalSettings?.leverageType ?? channelValue);
  const pendingGlobalSettings = {
    marginType: pendingGlobalMarginType,
    multiplier: pendingGlobalMultiplier,
    leverageType: pendingGlobalLeverageType,
  };
  const pendingSymbolsSettings = {
    marginType: pendingMarginType,
    multiplier: pendingMultiplier,
    leverageType: pendingLeverageType,
  };

  useEffect(() => {
    const shouldInitialize = getShouldInitializeData();
    if (shouldInitialize) {
      const initData = getInitData();
      setMultiSymbolMarginData?.(initData);
      let globalSettings = isSymbol(initData)
        ? undefined
        : initData?.[MARGIN_GLOBAL_SETTINGS_KEY];
      if (!globalSettings) globalSettings = getDefaultMarginData();
      setPendingGlobalMarginType(globalSettings.marginType);
      setPendingGlobalMultiplier(globalSettings.multiplier);
      setPendingGlobalLeverageType(globalSettings.leverageType);
      setInitializedExchange(isSymbol(account) ? undefined : account?.exchange);
      setInitializedBotGeneralData(originalBotMarginData);
      setShowOnlyModifiedSymbols(getDefaultShowModifiedSymbols());
    }
  }, [
    botGeneralData,
    multiSymbolMarginData,
    account,
    getBotCreateGeneralDataStatus?.isFetching,
  ]);

  const applyMaxMultiplier = <
    T extends SymbolMarginData | undefined | MarginData
  >(
    marginData: T,
    symbol: string,
    _maxMultiplier?: number
  ): T => {
    const maxMultiplier =
      _maxMultiplier ?? symbolToMarginMetadata?.[symbol]?.maxMultiplier;
    const multiplier = marginData?.multiplier;
    if (
      multiplier &&
      typeof multiplier === "number" &&
      typeof maxMultiplier === "number"
    ) {
      return {
        ...marginData,
        multiplier: Math.min(maxMultiplier, multiplier),
      };
    }
    return marginData;
  };

  const checkMaxMultiplierError = (
    dataWithMax?: MarginData,
    data?: MarginData
  ) => {
    return (
      typeof dataWithMax?.multiplier === "number" &&
      typeof data?.multiplier === "number" &&
      dataWithMax.multiplier < data.multiplier
    );
  };

  const getSymbolOriginalBotMultiplier = (symbol: string) => {
    let originalBotSymbolMarginData = isSymbol(originalBotMarginData)
      ? undefined
      : originalBotMarginData?.[symbol];
    if (!originalBotSymbolMarginData && symbol !== MARGIN_GLOBAL_SETTINGS_KEY) {
      const globalSettings = !isSymbol(originalBotMarginData)
        ? originalBotMarginData?.[MARGIN_GLOBAL_SETTINGS_KEY]
        : undefined;
      originalBotSymbolMarginData = applyMaxMultiplier(
        globalSettings ?? getDefaultMarginData(),
        symbol,
        getSymbolMetadata(symbol, originalBotExchange)?.leverage_max
      );
    }
    return originalBotSymbolMarginData?.multiplier ?? channelValue;
  };

  const getModifiedFromOriginalBotMarginData = (
    symbol: string,
    originalMultiplier: PendingMultiplier
  ) => {
    return originalMultiplier !== getSymbolOriginalBotMultiplier(symbol);
  };

  const getSymbolUpdatedData = (symbol: string) => {
    const originalMarginData = applyMaxMultiplier(
      multiSymbolMarginData?.[symbol] ??
        globalSettings ??
        getDefaultMarginData(),
      symbol
    )!;
    const symbolMarginMetadata = symbolToMarginMetadata?.[symbol] || {};
    let updatedData: SymbolMarginData = { ...originalMarginData };
    const isSelected = selectedSymbols.has(symbol);
    const isSymbolOverride = !!multiSymbolMarginData?.[symbol];
    if (isSelected) {
      if (pendingMarginType) updatedData.marginType = pendingMarginType;
      if (pendingMultiplier) {
        updatedData.multiplier = pendingMultiplier;
      }
      if (pendingLeverageType) {
        updatedData.leverageType = pendingLeverageType;
      }
      if (
        !isSymbol(updatedData.multiplier) &&
        isSymbol(updatedData.leverageType)
      ) {
        updatedData.leverageType = LeverageType.EXACT;
      }
      if (
        isSymbol(updatedData.multiplier) &&
        !isSymbol(updatedData.leverageType)
      ) {
        updatedData.multiplier = 1;
      }
    } else if (!isSymbolOverride) {
      if (pendingGlobalMarginType)
        updatedData.marginType = pendingGlobalMarginType;
      if (pendingGlobalMultiplier)
        updatedData.multiplier = pendingGlobalMultiplier;
      if (pendingGlobalLeverageType)
        updatedData.leverageType = pendingGlobalLeverageType;
    }

    const updatedDataWithMax = applyMaxMultiplier(updatedData, symbol);
    const valueChangedFromOriginal = getModifiedFromOriginalBotMarginData(
      symbol,
      originalMarginData.multiplier
    );
    const lastModifiedSymbolMarginData =
      lastModifiedMultiSymbolMarginData?.[symbol];
    const hasLeverageValueModifiedError =
      valueChangedFromOriginal &&
      (isCopyPublic ||
        (isDuplicate && !lastModifiedSymbolMarginData?.multiplier));

    const isOriginalGlobalSettingsSymbol = !multiSymbolMarginData?.[symbol];
    const isGlobalSettingsSymbol =
      isOriginalGlobalSettingsSymbol && !selectedSymbols.has(symbol);
    const hasPendingChanges =
      !isSymbolsOnly &&
      (!isEqual(updatedDataWithMax, originalMarginData) ||
        isGlobalSettingsSymbol !== isOriginalGlobalSettingsSymbol);

    const hasMaxGlobalLeverageError =
      !isSymbolsOnly &&
      isGlobalSettingsSymbol &&
      !isSymbolOverride &&
      checkMaxMultiplierError(updatedDataWithMax, pendingGlobalSettings);
    const hasMaxLeverageError =
      !isSymbolsOnly &&
      isSelected &&
      checkMaxMultiplierError(updatedDataWithMax, pendingSymbolsSettings);

    return {
      ...symbolMarginMetadata,
      originalMarginType: originalMarginData.marginType,
      originalMultiplier: originalMarginData.multiplier,
      originalLeverageType: originalMarginData.leverageType,
      multiplier: updatedDataWithMax.multiplier,
      marginType: updatedDataWithMax.marginType,
      leverageType: updatedDataWithMax.leverageType,
      hasPendingChanges,
      hasMaxLeverageError: hasMaxLeverageError || hasMaxGlobalLeverageError,
      hasLeverageValueModifiedError,
      isGlobalSettingsSymbol,
      isOriginalGlobalSettingsSymbol,
    };
  };

  const updatedMultiSymbolMarginData: SymbolToUpdatedMarginData =
    useMemo(() => {
      return [...allSymbols, MARGIN_GLOBAL_SETTINGS_KEY].reduce(
        (symbolToData: SymbolToUpdatedMarginData, symbol) => {
          symbolToData[symbol] = getSymbolUpdatedData(symbol);
          return symbolToData;
        },
        {}
      );
    }, [
      allSymbols,
      multiSymbolMarginData,
      selectedSymbols,
      pendingMultiplier,
      pendingMarginType,
      pendingLeverageType,
      pendingGlobalMarginType,
      pendingGlobalMultiplier,
      pendingGlobalLeverageType,
      lastModifiedMultiSymbolMarginData,
    ]);

  const filteredSymbols = useMemo(() => {
    if (showOnlyModifiedSymbols) {
      return allSymbols.filter((symbol) => {
        const updatedData = updatedMultiSymbolMarginData[symbol];
        return !updatedData?.isGlobalSettingsSymbol;
      });
    }
    return allSymbols;
  }, [allSymbols, showOnlyModifiedSymbols, updatedMultiSymbolMarginData]);

  const searchFilteredSymbols = useMemo(() => {
    return filteredSymbols.filter((symbol) => {
      return checkSymbolSearch(symbol, searchValue);
    });
  }, [filteredSymbols, searchValue]);

  useEffect(() => {
    const filteredSelectedSymbols = new Set(
      filteredSymbols.filter((symbol) => selectedSymbols.has(symbol))
    );
    if (filteredSelectedSymbols.size !== selectedSymbols.size) {
      setSelectedSymbols(filteredSelectedSymbols);
    }
  }, [filteredSymbols]);

  const getSymbolsMarginOptions = (symbols: Set<string>) => {
    const marginTypes = new Set<MarginData["marginType"]>();
    const multipliers = new Set<MarginData["multiplier"]>();
    const leverageTypes = new Set<MarginData["leverageType"]>();
    symbols.forEach((symbol) => {
      const { marginType, leverageType, multiplier } =
        updatedMultiSymbolMarginData?.[symbol] || getDefaultMarginData();
      if (marginType) marginTypes.add(marginType);
      if (multiplier) multipliers.add(multiplier);
      if (leverageType) leverageTypes.add(leverageType);
    });
    return {
      marginTypes: [...marginTypes],
      multipliers: [...multipliers],
      leverageTypes: [...leverageTypes],
    };
  };

  const setPendingValuesFromSymbols = (symbols: Set<string>) => {
    if (!symbols.size) return;
    const { marginTypes, multipliers, leverageTypes } =
      getSymbolsMarginOptions(symbols);
    if (
      !pendingMarginType ||
      (marginTypes.length > 1 && !modifiedPendingValues.has("marginType"))
    ) {
      let marginType: MarginData["marginType"] = [...marginTypes]?.[0];
      if (marginTypes.length > 1) marginType = undefined;
      setPendingMarginType(marginType);
    }

    let multiplier: MarginData["multiplier"] = [...multipliers]?.[0];
    if (multipliers.length > 1 && !modifiedPendingValues.has("multiplier"))
      multiplier = undefined;
    if (
      !pendingMultiplier ||
      (multipliers.length > 1 && !modifiedPendingValues.has("multiplier"))
    ) {
      setPendingMultiplier(multiplier);
    }

    if (
      !pendingLeverageType ||
      (leverageTypes.length > 1 && !modifiedPendingValues.has("leverageType"))
    ) {
      let leverageType: MarginData["leverageType"] =
        [...leverageTypes]?.[0] ??
        (multiplier === channelValue ? channelValue : LeverageType.EXACT);
      if (leverageTypes.length > 1) leverageType = undefined;
      setPendingLeverageType(leverageType);
    }
  };

  const setSelectedSymbolsAndPendingValues = (symbols: Set<string>) => {
    if (symbols.size && !selectedSymbols.size && globalSettingsExpanded) {
      setGlobalSettingsExpanded(false);
    }
    setSelectedSymbols(symbols);
    if (symbols.size) {
      setPendingValuesFromSymbols(symbols);
    } else {
      resetData(false, true);
    }
  };

  const onSymbolClick = (symbol: string) => {
    setSelectedSymbolsAndPendingValues(toggleSetItem(selectedSymbols, symbol));
  };

  const [
    symbolOptions,
    hasMaxMultiplierError,
    hasLeverageValueModifiedError,
    hasPendingChanges,
    selectorPreview,
  ] = useMemo(() => {
    if (!isSignalsBot) return [undefined, false, false, false, ""];
    let hasMaxMultiplierError = false;
    let hasLeverageValueModifiedError = false;
    let hasPendingChanges = false;
    const baseMarginData =
      multiSymbolMarginData?.[filteredSymbols[0]] ??
      multiSymbolMarginData?.[MARGIN_GLOBAL_SETTINGS_KEY];
    let {
      marginType: baseMarginType,
      multiplier: baseMultiplier,
      leverageType: baseLeverageType,
    } = baseMarginData || {};
    const processPreviewMarginData = (marginData?: MarginData) => {
      if (!marginData) return;
      if (baseMarginType && baseMarginType !== marginData?.marginType) {
        baseMarginType = undefined;
      }
      if (baseMultiplier && baseMultiplier !== marginData?.multiplier) {
        baseMultiplier = undefined;
      }
      if (baseLeverageType && baseLeverageType !== marginData?.leverageType) {
        baseLeverageType = undefined;
      }
    };
    const symbols = isViewOnly
      ? searchFilteredSymbols.filter((symbol) => {
          return !selectedSymbols.size || selectedSymbols.has(symbol);
        })
      : searchFilteredSymbols;
    const options = symbols.map((symbol) => {
      const symbolMarginData = multiSymbolMarginData?.[symbol];
      const updatedMarginData = updatedMultiSymbolMarginData[symbol];
      const isSelected = selectedSymbols.has(symbol);
      processPreviewMarginData(symbolMarginData);
      const {
        hasMaxLeverageError: symbolHasMaxLeverageError,
        hasPendingChanges: symbolHasPendingChanges,
        hasLeverageValueModifiedError: symbolHasLeverageValueModifiedError,
        isGlobalSettingsSymbol,
      } = updatedMultiSymbolMarginData[symbol] || {};
      let color: string | undefined;
      if (symbolHasMaxLeverageError || symbolHasLeverageValueModifiedError) {
        color = theme.colors.darkYellow;
      }
      if (symbolHasMaxLeverageError) {
        hasMaxMultiplierError = true;
      }
      if (symbolHasLeverageValueModifiedError) {
        hasLeverageValueModifiedError = true;
      }
      if (symbolHasPendingChanges) hasPendingChanges = true;

      return {
        onClick: () => onSymbolClick(symbol),
        isSelected,
        symbol,
        marginDisplay: getSymbolMarginPreview(updatedMarginData),
        hasMaxLeverageError: !!symbolHasMaxLeverageError,
        hasLeverageValueModifiedError: !!symbolHasLeverageValueModifiedError,
        hasPendingChanges: !!symbolHasPendingChanges,
        color,
        isGlobalSettingsSymbol,
      };
    });

    processPreviewMarginData(globalSettings);
    let selectorPreview = "Multiple Leverages";
    if (
      baseMarginData &&
      baseMarginType &&
      baseMultiplier &&
      baseLeverageType
    ) {
      const marginPreviewData = getSymbolMarginPreview(baseMarginData);
      selectorPreview =
        typeof marginPreviewData === "string"
          ? marginPreviewData
          : `${marginPreviewData.marginType} - ${marginPreviewData.multiplier}`;
    }
    const haveGlobalPendingChanges = !isEqual(
      pendingGlobalSettings,
      globalSettings
    );
    return [
      options,
      hasMaxMultiplierError,
      hasLeverageValueModifiedError,
      hasPendingChanges || haveGlobalPendingChanges,
      selectorPreview,
    ];
  }, [
    multiSymbolMarginData,
    searchValue,
    selectedSymbols,
    pendingMarginType,
    pendingMultiplier,
    pendingLeverageType,
    pendingGlobalMarginType,
    pendingGlobalMultiplier,
    pendingGlobalLeverageType,
    searchFilteredSymbols,
    updatedMultiSymbolMarginData,
  ]);

  const updateLastModifiedData = (
    updatedLastModifiedData: MultiSymbolMarginData
  ) => {
    setLastModifiedMultiSymbolMarginData?.(
      (lastModifiedMultiSymbolMarginData) => ({
        ...lastModifiedMultiSymbolMarginData,
        ...updatedLastModifiedData,
      })
    );
    propsUpdateLastModifiedData?.(updatedLastModifiedData);
  };

  const value = useMemo(() => {
    return {
      botGeneralData,
      pendingGlobalMarginType,
      setPendingGlobalMarginType,
      pendingGlobalMultiplier,
      setPendingGlobalMultiplier,
      pendingGlobalLeverageType,
      setPendingGlobalLeverageType,
      pendingMarginType,
      setPendingMarginType,
      pendingMultiplier,
      setPendingMultiplier,
      pendingLeverageType,
      setPendingLeverageType,
      isSymbolsOnly,
      isViewOnly,
      selectedSymbols,
      setSelectedSymbols,
      modifiedPendingValues,
      setModifiedPendingValues,
      searchValue,
      setSearchValue,
      isSearchOpen,
      setIsSearchOpen,
      resetData,
      showOnlyModifiedSymbols,
      setShowOnlyModifiedSymbols,
      allSymbols,
      filteredSymbols,
      searchFilteredSymbols,
      botFormDataRef,
      multiSymbolMarginData,
      updatedMultiSymbolMarginData,
      setSelectedSymbolsAndPendingValues,
      lastModifiedMultiSymbolMarginData,
      hasMaxMultiplierError,
      hasLeverageValueModifiedError,
      hasPendingChanges,
      selectorPreview,
      symbolOptions,
      account,
      alwaysDisplay,
      botData,
      globalSettingsExpanded,
      setGlobalSettingsExpanded,
      symbolToMarginMetadata,
      initializedExchange,
      isFullScreen,
      updateLastModifiedData,
      applyMaxMultiplier,
      getSymbolOriginalBotMultiplier,
    };
  }, [
    botGeneralData,
    pendingGlobalMarginType,
    pendingGlobalMultiplier,
    pendingGlobalLeverageType,
    pendingMarginType,
    pendingMultiplier,
    pendingLeverageType,
    isSymbolsOnly,
    isViewOnly,
    selectedSymbols,
    modifiedPendingValues,
    isSearchOpen,
    searchValue,
    showOnlyModifiedSymbols,
    allSymbols,
    filteredSymbols,
    searchFilteredSymbols,
    multiSymbolMarginData,
    updatedMultiSymbolMarginData,
    lastModifiedMultiSymbolMarginData,
    hasMaxMultiplierError,
    hasLeverageValueModifiedError,
    hasPendingChanges,
    selectorPreview,
    symbolOptions,
    account,
    alwaysDisplay,
    botData,
    globalSettingsExpanded,
    initializedExchange,
    isFullScreen,
  ]);

  return (
    <MultiSymbolMarginContext.Provider value={value}>
      {children}
    </MultiSymbolMarginContext.Provider>
  );
};

export default MultiSymbolMarginProvider;
